import { Download } from "lucide-react";
import type { ReactNode } from "react";

export type ChannelRow = {
  label: string;
  detail: string;
  countLabel: string;
  count: number;
  valueLabel: string;
  value: number;
  share: number;
  variant?: "default" | "total";
  onDownload?: () => void;
};

export const formatChannelCurrency = (value: number, currency = "COP") =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(value || 0);

export const formatChannelNumber = (value: number) =>
  new Intl.NumberFormat("es-CO", { maximumFractionDigits: 0 }).format(value || 0);

export const formatChannelPercent = (value: number) =>
  `${new Intl.NumberFormat("es-CO", { maximumFractionDigits: 1 }).format(value || 0)}%`;

export function ChannelTable({
  title,
  subtitle,
  rows,
  currency,
}: {
  title: string;
  subtitle: string;
  rows: ChannelRow[];
  currency: string;
}) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-100 px-5 py-4">
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-[900px] text-left text-sm lg:min-w-full">
          <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
            <tr>
              <TableHead className="w-[38%]">Segmento</TableHead>
              <TableHead className="w-[16%]" align="right">Volumen</TableHead>
              <TableHead className="w-[20%]" align="right">Valor</TableHead>
              <TableHead className="w-[18%]" align="right">Participación</TableHead>
              <TableHead className="w-[8%]" align="right">Descargar</TableHead>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((row) => {
              const isTotal = row.variant === "total";
              return (
                <tr
                  key={row.label}
                  className={`align-top ${isTotal ? "border-t border-gray-200 bg-gray-100/60" : ""}`}
                >
                  <td className="px-5 py-4">
                    <p className={`font-semibold ${isTotal ? "text-gray-950" : "text-gray-900"}`}>{row.label}</p>
                    <p className="mt-1 max-w-[360px] text-xs leading-5 text-gray-500">{row.detail}</p>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <p className={`font-semibold ${isTotal ? "text-gray-950" : "text-gray-900"}`}>
                      {formatChannelNumber(row.count)}
                    </p>
                    <p className="mt-1 text-xs text-gray-400">{row.countLabel}</p>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <p className={`font-semibold ${isTotal ? "text-gray-950" : "text-gray-900"}`}>
                      {formatChannelCurrency(row.value, currency)}
                    </p>
                    <p className="mt-1 text-xs text-gray-400">{row.valueLabel}</p>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <p className={`font-semibold ${isTotal ? "text-gray-950" : "text-gray-900"}`}>
                      {formatChannelPercent(row.share)}
                    </p>
                    <div className="ml-auto mt-2 h-1.5 w-full max-w-[150px] overflow-hidden rounded-full bg-gray-100">
                      <div
                        className="h-full rounded-full bg-[#2b5ce6]"
                        style={{ width: `${Math.max(0, Math.min(row.share, 100))}%` }}
                      />
                    </div>
                  </td>
                  <td className="px-5 py-4 text-right">
                    {row.onDownload ? (
                      <button
                        type="button"
                        onClick={row.onDownload}
                        title={`Descargar ${row.label}`}
                        aria-label={`Descargar ${row.label}`}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 shadow-sm transition hover:bg-gray-50 hover:text-[#2b5ce6]"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function TableHead({
  children,
  align = "left",
  className = "",
}: {
  children: ReactNode;
  align?: "left" | "right";
  className?: string;
}) {
  return (
    <th className={`px-5 py-3 font-semibold ${align === "right" ? "text-right" : "text-left"} ${className}`}>
      {children}
    </th>
  );
}
